/**
 * Parent-portal admin (ADR-0015, choice #5) — the operator editor for the
 * instance-level TENANT brand that the launcher + slug-less login wear. Lives
 * in the Apps plane of the sidebar (it federates the apps).
 *
 * Edits: tenant name, logo URL, accent + page-background colours, the login
 * screen's title/subtitle, and the master switch for the in-app app-switcher.
 * Saved wholesale via PUT (the backend stores the whole brand document).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { parentPortalApi, type TenantBrand } from "@/lib/api-parent-portal";

const EMPTY: TenantBrand = {
  name: null,
  logo_url: null,
  theme: {},
  login: { title: null, subtitle: null },
  footer: null,
  switcher_enabled: true,
};

/** A row with a label/help on the left and a toggle on the right. */
function ToggleRow({
  title,
  help,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  help: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <p className="mt-0.5 text-xs text-muted-foreground">{help}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

/** A labelled text field; empty string round-trips to `null` on save. */
function Field({
  label,
  value,
  onChange,
  placeholder,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  help?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {help && <span className="mt-1 block text-xs text-muted-foreground/70">{help}</span>}
    </label>
  );
}

/** A colour field: a native swatch + a hex text input, kept in sync. */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded border border-border bg-surface p-0.5"
          aria-label={`${label} swatch`}
        />
        <Input value={value} placeholder="#0ea5e9" onChange={(e) => onChange(e.target.value)} />
      </div>
    </label>
  );
}

export function ParentPortalPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<TenantBrand>(EMPTY);
  const [saved, setSaved] = useState(false);

  const brandQ = useQuery({
    queryKey: ["admin", "parent-brand"],
    queryFn: () => parentPortalApi.getBrand(),
  });

  // Hydrate the form once the brand loads.
  useEffect(() => {
    if (brandQ.data) setDraft(brandQ.data);
  }, [brandQ.data]);

  const save = useMutation({
    mutationFn: (body: TenantBrand) => parentPortalApi.updateBrand(body),
    onSuccess: (data) => {
      qc.setQueryData(["admin", "parent-brand"], data);
      // The public/switcher queries read the same brand — drop their caches.
      qc.invalidateQueries({ queryKey: ["parent-brand"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const setTheme = (key: string, v: string) =>
    setDraft((d) => ({ ...d, theme: { ...d.theme, [key]: v } }));
  const setLogin = (key: "title" | "subtitle", v: string) =>
    setDraft((d) => ({ ...d, login: { ...d.login, [key]: v || null } }));

  function onSave() {
    // Normalise empty strings → null so the stored doc stays clean.
    save.mutate({
      ...draft,
      name: draft.name?.trim() || null,
      logo_url: draft.logo_url?.trim() || null,
      theme: Object.fromEntries(
        Object.entries(draft.theme).filter(([, v]) => typeof v === "string" && v.trim()),
      ),
    });
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("parentPortal.title")}
        description={t("parentPortal.subtitle")}
        actions={
          <Button onClick={onSave} disabled={save.isPending || brandQ.isPending}>
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <Check className="h-4 w-4" />
            ) : null}
            {saved ? t("parentPortal.saved") : t("common.save")}
          </Button>
        }
      />
      <PageContent>
        <div className="mx-auto grid max-w-2xl gap-6">
          {/* Brand */}
          <section className="grid gap-4 rounded-xl border border-border bg-surface p-5">
            <h2 className="text-sm font-semibold text-foreground">{t("parentPortal.brand")}</h2>
            <Field
              label={t("parentPortal.name")}
              value={draft.name ?? ""}
              onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
              placeholder="Acme Group"
              help={t("parentPortal.nameHelp")}
            />
            <Field
              label={t("parentPortal.logoUrl")}
              value={draft.logo_url ?? ""}
              onChange={(v) => setDraft((d) => ({ ...d, logo_url: v }))}
              placeholder="https://…/logo.svg"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <ColorField
                label={t("parentPortal.accent")}
                value={draft.theme.accent ?? ""}
                onChange={(v) => setTheme("accent", v)}
              />
              <ColorField
                label={t("parentPortal.background")}
                value={draft.theme.background ?? ""}
                onChange={(v) => setTheme("background", v)}
              />
            </div>
            <Field
              label={t("parentPortal.footer")}
              value={draft.footer ?? ""}
              onChange={(v) => setDraft((d) => ({ ...d, footer: v || null }))}
              placeholder="© Acme Group"
              help={t("parentPortal.footerHelp")}
            />
          </section>

          {/* Login copy */}
          <section className="grid gap-4 rounded-xl border border-border bg-surface p-5">
            <h2 className="text-sm font-semibold text-foreground">{t("parentPortal.login")}</h2>
            <Field
              label={t("parentPortal.loginTitle")}
              value={draft.login.title ?? ""}
              onChange={(v) => setLogin("title", v)}
              placeholder="Welcome to Acme"
            />
            <Field
              label={t("parentPortal.loginSubtitle")}
              value={draft.login.subtitle ?? ""}
              onChange={(v) => setLogin("subtitle", v)}
              placeholder="Sign in to reach all of your apps."
            />
          </section>

          {/* Switcher */}
          <section className="grid gap-4 rounded-xl border border-border bg-surface p-5">
            <h2 className="text-sm font-semibold text-foreground">{t("parentPortal.switcher")}</h2>
            <ToggleRow
              title={t("parentPortal.switcher")}
              help={t("parentPortal.switcherHelp")}
              checked={draft.switcher_enabled}
              onChange={(v) => setDraft((d) => ({ ...d, switcher_enabled: v }))}
            />
          </section>
        </div>
      </PageContent>
    </PageContainer>
  );
}
